<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxBadge,
  NxBanner,
  NxButton,
  NxChip,
  NxChipGroup,
  NxSettingsToggle,
  NxTable,
  NxTableColumn,
  downloadBlob,
} from '@blpde/nx-ui';
import {
  searchSynonymEntries,
  searchSynonymRemoval,
  type SearchSynonymEntry,
  type SearchSynonymsConfig,
} from '../../config/searchSynonyms';
import { parseSynonymsCsv, synonymsCsvText, type DictionaryLanguage } from '../dictionaryFile';
import { dictionaryFold } from '../dictionaryFold';
import { synonymsImportPreview } from '../dictionaryImport';
import type { DictionaryLevelHandle } from './level';
import { DICTIONARY_TEXT, languageLabel, originLabel, originVariant } from './labels';
import { synonymDraftPatch, synonymDraftProblem, type SynonymDraft } from './drafts';
import { DICTIONARY_PAGE_SIZE, usePagedRows } from './paging';
import { useDictionaryTabState } from './tabState';
import DictionaryFillHint from './DictionaryFillHint.vue';
import DictionaryImportModal from './DictionaryImportModal.vue';
import DictionaryToolbar from './DictionaryToolbar.vue';
import ExplainPanel from './ExplainPanel.vue';
import SynonymEditorPanel from './SynonymEditorPanel.vue';

/**
 * „Synonyme“ (I-3024): the words the search treats alike — `search.synonyms`
 * of one layer, groups by language. A two-way group finds every term from
 * every other, a one-way group finds the others from its first term only.
 * The groups of a farther layer show their origin and stay; a lock of the
 * field by a layer before the written one makes the tab read-only. A side
 * panel edits one group (with OpenThesaurus proposals), the import and export
 * are CSV (`name;sprache;richtung;begriff1|begriff2|…`).
 */
const props = defineProps<{ level: DictionaryLevelHandle<SearchSynonymsConfig> }>();

const { t } = useI18n();
const T = DICTIONARY_TEXT;
const FIELD = 'synonyms';
const state = useDictionaryTabState(props.level as DictionaryLevelHandle<unknown>, FIELD);

interface Row extends SearchSynonymEntry, Record<string, unknown> {
  id: string;
}

const search = ref('');
const lang = ref<DictionaryLanguage | 'all'>('all');
const entries = computed(() =>
  searchSynonymEntries(props.level.effective.value, props.level.layers.value, props.level.target.value),
);
const ownEntries = computed(() => entries.value.filter((entry) => entry.source === 'own'));
const rows = computed<Row[]>(() => {
  const needle = dictionaryFold(search.value.trim());
  return entries.value
    .filter((entry) => lang.value === 'all' || entry.lang === lang.value)
    .filter(
      (entry) =>
        !needle ||
        dictionaryFold(entry.name).includes(needle) ||
        entry.terms.some((term) => dictionaryFold(term).includes(needle)),
    )
    .map((entry) => ({ ...entry, id: `${entry.lang}:${entry.name}` }));
});
const paged = usePagedRows(() => rows.value);
const table = paged.table;
watch([search, lang], () => paged.reset());

function asRow(value: unknown): Row {
  return value as Row;
}

const editable = computed(() => !state.readOnly.value && state.layersKnown.value);

/* ── Editing ── */

interface Draft {
  editing: { lang: DictionaryLanguage; name: string } | null;
  name: string;
  lang: DictionaryLanguage;
  oneWay: boolean;
  source: string;
  terms: string[];
  key: number;
}
const draft = ref<Draft | null>(null);
let draftKey = 0;

function openNew(): void {
  draft.value = {
    editing: null,
    name: '',
    lang: lang.value === 'en' ? 'en' : 'de',
    oneWay: false,
    source: '',
    terms: [],
    key: (draftKey += 1),
  };
}

function openEdit(row: { lang: DictionaryLanguage; name: string; oneWay: boolean; terms: readonly string[] }): void {
  draft.value = {
    editing: { lang: row.lang, name: row.name },
    name: row.name,
    lang: row.lang,
    oneWay: row.oneWay,
    // A one-way group's first term is its source.
    source: row.oneWay ? (row.terms[0] ?? '') : '',
    terms: row.oneWay ? row.terms.slice(1) : [...row.terms],
    key: (draftKey += 1),
  };
}

/** The group a conflict names, opened in the panel (an own group of the written layer). */
function openGroup(name: string): void {
  const target = ownEntries.value.find((entry) => entry.lang === draft.value?.lang && entry.name === name);
  if (target) openEdit(target);
}

function problemOf(next: SynonymDraft) {
  return synonymDraftProblem(next, ownEntries.value, draft.value?.editing ?? undefined);
}

function save(next: SynonymDraft) {
  return props.level.patch(synonymDraftPatch(next, draft.value?.editing ?? undefined));
}

/** Only an own row of an editable layer can be changed; a farther layer's row stays as it is. */
function isOwnEditable(row: Row): boolean {
  return editable.value && row.source === 'own';
}

/** Double-click (or Enter) on a row opens the side panel of an own row; an inherited row stays read-only. */
function onRowOpen(_rowId: string, _columnId: string, row: Row): void {
  if (isOwnEditable(row)) openEdit(row);
}

/**
 * The kit's pinned X (after its confirmation). `false` keeps the row in the
 * table: a refused write already showed the standard write-failure toast.
 */
async function onDelete({ id }: { id: string }): Promise<boolean> {
  const row = rows.value.find((entry) => entry.id === id);
  if (!row || !isOwnEditable(row)) return false;
  return state.write(searchSynonymRemoval(row.lang, row.name));
}

/* ── Import and export ── */

const importOpen = ref(false);

function previewImport(text: string) {
  const defaults: DictionaryLanguage = lang.value === 'en' ? 'en' : 'de';
  return synonymsImportPreview(parseSynonymsCsv(text, defaults).rows, state.ownDocument.value ?? {});
}

function exportFile(): void {
  downloadBlob(synonymsCsvText(ownEntries.value), t(`${T}.export.synonyms`));
}

const levelLabel = computed(() => t(`${T}.level.${props.level.key}`));
</script>

<template>
  <div class="dict" :class="{ 'dict--editor': draft }" data-testid="dictionary-synonyms">
    <div class="dict-main">
      <p class="dict-note">{{ t(`${T}.hints.synonyms`) }}</p>
      <NxBanner
        v-if="state.readOnlyText.value"
        variant="info"
        :title="state.readOnlyText.value"
        data-testid="dictionary-readonly"
      />
      <NxSettingsToggle
        v-if="level.setLock"
        :model-value="state.ownLocked.value"
        :label="t(`${T}.lock.label`)"
        :disabled="!editable"
        data-testid="dictionary-lock"
        @update:model-value="(on: boolean) => state.setOwnLock(on)"
      />
      <DictionaryToolbar v-model:search="search" v-model:lang="lang">
        <NxButton data-testid="dictionary-add-group" :disabled="!editable" @click="openNew">
          {{ t(`${T}.actions.addGroup`) }}
        </NxButton>
        <NxButton variant="outline" data-testid="dictionary-import" :disabled="!editable" @click="importOpen = true">
          {{ t(`${T}.actions.import`) }}
        </NxButton>
        <NxButton variant="outline" data-testid="dictionary-export" :disabled="!ownEntries.length" @click="exportFile">
          {{ t(`${T}.actions.export`) }}
        </NxButton>
      </DictionaryToolbar>
      <div class="dict-rows">
        <!-- @vue-generic {Row} -->
        <NxTable
          ref="table"
          :data="paged.pageRows.value as Row[]"
          :total-count="paged.total.value"
          :row-id-key="(row: Row) => row.id"
          :pagination-limit="DICTIONARY_PAGE_SIZE"
          :pagination-page-sizes="[DICTIONARY_PAGE_SIZE]"
          pagination-mode="pages"
          hide-create-button
          :on-delete="onDelete"
          :row-deletable="isOwnEditable"
          :on-table-state-change="paged.onState"
          @cell-double-click="onRowOpen"
        >
          <NxTableColumn
            column-id="name"
            :display-name="t(`${T}.columns.name`)"
            :filterable="false"
            :min-width="130"
            :to-csv-string="(row: Row) => row.name"
          >
            <template #cell="{ rowData }">
              <span data-testid="dictionary-group-name">{{ asRow(rowData).name }}</span>
            </template>
          </NxTableColumn>
          <NxTableColumn
            column-id="direction"
            :display-name="t(`${T}.columns.direction`)"
            :filterable="false"
            :min-width="130"
            :to-csv-string="(row: Row) => (row.oneWay ? 'one_way' : 'two_way')"
          >
            <template #cell="{ rowData }">
              <NxBadge :variant="asRow(rowData).oneWay ? 'neutral' : 'info'">
                {{ asRow(rowData).oneWay ? t(`${T}.oneWay`) : t(`${T}.bothWays`) }}
              </NxBadge>
            </template>
          </NxTableColumn>
          <NxTableColumn
            column-id="terms"
            :display-name="t(`${T}.columns.terms`)"
            :filterable="false"
            :min-width="260"
            :to-csv-string="(row: Row) => row.terms.join(', ')"
          >
            <template #cell="{ rowData }">
              <NxChipGroup shrink>
                <NxChip
                  v-for="(term, index) in asRow(rowData).terms"
                  :key="term"
                  static
                  size="xs"
                  :selected="asRow(rowData).oneWay && index === 0"
                >
                  {{ term }}
                </NxChip>
              </NxChipGroup>
            </template>
          </NxTableColumn>
          <NxTableColumn
            column-id="lang"
            :display-name="t(`${T}.columns.language`)"
            :filterable="false"
            :to-csv-string="(row: Row) => row.lang"
          >
            <template #cell="{ rowData }">{{ languageLabel(asRow(rowData).lang) }}</template>
          </NxTableColumn>
          <NxTableColumn
            column-id="origin"
            :display-name="t(`${T}.columns.origin`)"
            :filterable="false"
            :min-width="140"
            :to-csv-string="(row: Row) => originLabel(t, row)"
          >
            <template #cell="{ rowData }">
              <NxBadge
                :variant="originVariant(asRow(rowData))"
                :title="asRow(rowData).source === 'own' ? undefined : t(`${T}.origin.inheritedHint`)"
                data-testid="dictionary-origin"
              >
                {{ originLabel(t, asRow(rowData)) }}
              </NxBadge>
            </template>
          </NxTableColumn>
        </NxTable>
      </div>
      <DictionaryFillHint :document="state.ownDocument.value" kind="synonyms" />
    </div>
    <SynonymEditorPanel
      v-if="draft"
      :key="draft.key"
      :editing="draft.editing"
      :name="draft.name"
      :lang="draft.lang"
      :one-way="draft.oneWay"
      :source="draft.source"
      :terms="draft.terms"
      :level-label="levelLabel"
      :problem="problemOf"
      :save="save"
      @close="draft = null"
      @reload="draft = null"
      @open-group="openGroup"
    />
    <ExplainPanel v-else />
    <DictionaryImportModal
      v-model:open="importOpen"
      :title="t(`${T}.import.titleSynonyms`)"
      :hint="t(`${T}.import.formatSynonyms`)"
      accept=".csv,.txt,text/csv,text/plain"
      :preview="previewImport"
      :apply="(patch) => level.patch(patch)"
    />
  </div>
</template>

<style scoped>
.dict {
  display: grid;
  grid-template-columns: minmax(0, 1fr) calc(var(--nxt-size-unit) * 23.75);
  gap: var(--nxt-space-sm);
  align-items: start;
}
.dict--editor {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
}
.dict-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}
.dict-note {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.dict-rows {
  height: calc(var(--nxt-size-unit) * 26.875);
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
